/* Companio UGC tiles — play only what is actually on screen.

   Four silent portrait videos decoding at once is a real cost in battery and
   data, and most of them are off screen most of the time. Everything here is
   about not paying for pixels nobody is looking at.

   WHAT IT DOES
   - Shopify-hosted <video>: the file URL sits in data-ugc-src and is only
     assigned when the tile reaches the screen, so nothing is fetched for a tile
     the visitor never scrolls to. Play on enter, pause on leave.
   - YouTube / Vimeo: the iframe src is held the same way and set on first
     appearance. Once mounted the embed stays, because tearing an iframe down
     and rebuilding it reloads the whole player; instead we ask it to pause over
     postMessage when it scrolls away. This is exactly why the Shopify-hosted
     route is the recommended one — we own that element and can stop it
     properly.

   ONE OBSERVER, ROOT = VIEWPORT.
   IntersectionObserver clips against scrolling ancestors as well as the
   viewport, so a tile that has drifted out of the rail's horizontal overflow
   reports as not intersecting without any extra bookkeeping.

   WHEN IT DOES NOTHING AT ALL
   prefers-reduced-motion, or the browser reporting Save-Data. Both leave the
   poster image in place, which is a complete and honest tile on its own.

   RAIL CLONES: companio-rail.js duplicates tiles to make the loop seamless. A
   cloned <video> would be a second stream of the same file, so on the
   companio:rail:cloned event we swap media inside clones for their poster.
   Visually identical, half the decoding.
*/
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  function saveData() {
    return !!(navigator.connection && navigator.connection.saveData);
  }

  function mountVideo(el) {
    if (!el.dataset.ugcMounted) {
      var src = el.dataset.ugcSrc;
      if (!src) return;
      el.src = src;
      el.dataset.ugcMounted = '1';
      el.load();
    }
    var playing = el.play();
    if (playing && playing.catch) {
      playing.catch(function () { /* the browser refused autoplay — poster stays */ });
    }
  }

  function mountEmbed(el) {
    if (el.dataset.ugcMounted) {
      command(el, 'play');
      return;
    }
    var src = el.dataset.ugcSrc;
    if (!src) return;
    el.src = src;
    el.dataset.ugcMounted = '1';
  }

  function command(iframe, action) {
    var win = iframe.contentWindow;
    if (!win) return;
    var kind = iframe.dataset.ugcEmbed;
    try {
      if (kind === 'youtube') {
        win.postMessage(JSON.stringify({
          event: 'command',
          func: action === 'play' ? 'playVideo' : 'pauseVideo',
          args: []
        }), '*');
      } else if (kind === 'vimeo') {
        win.postMessage(JSON.stringify({ method: action === 'play' ? 'play' : 'pause' }), '*');
      }
    } catch (error) {
      /* cross-origin frame not ready yet — harmless */
    }
  }

  function enter(el) {
    if (el.tagName === 'VIDEO') mountVideo(el);
    else mountEmbed(el);
  }

  function leave(el) {
    if (el.tagName === 'VIDEO') {
      if (!el.paused) el.pause();
    } else if (el.dataset.ugcMounted) {
      command(el, 'pause');
    }
  }

  function stripClones(root) {
    root.querySelectorAll('[data-rail-clone] [data-ugc-media]').forEach(function (el) {
      var poster = el.getAttribute('poster');
      if (el.tagName === 'VIDEO' && poster) {
        var img = document.createElement('img');
        img.src = poster;
        img.alt = '';
        img.className = el.className;
        img.setAttribute('aria-hidden', 'true');
        el.replaceWith(img);
      } else {
        el.remove();
      }
    });
  }

  function initUgc(root) {
    if (root.dataset.ugcReady) return;
    root.dataset.ugcReady = '1';

    root.addEventListener('companio:rail:cloned', function () { stripClones(root); });

    if (REDUCE.matches || saveData() || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) enter(entry.target);
        else leave(entry.target);
      });
    }, { threshold: 0.35 });

    root.querySelectorAll('[data-ugc-media]').forEach(function (el) {
      observer.observe(el);
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) return;
      root.querySelectorAll('[data-ugc-media]').forEach(leave);
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-ugc]').forEach(initUgc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
